/**
 * The tour's state machine. Pure: the runtime feeds it events and carries out
 * what each phase means. The step is the record's; the machine only follows
 * it (`STEP`), so a stamp that never lands cannot leave the two apart.
 */

export type Phase =
  /** The scene is being built; no card. */
  | "setting"
  /** Card up; the scene's allowed actions respond. */
  | "live"
  /** The person acted; the world has not caught up. */
  | "pending"
  /** The step's fact holds; the card shows it for a beat. */
  | "done"
  /** The card exits; the next stamp is being written. */
  | "leaving"
  | "failed"
  | "exited"
  | "finished";

export interface DirectorState {
  step: number;
  total: number;
  phase: Phase;
  /** Bumps whenever the scene has to be set again in place. */
  rev: number;
  /** Blocked actions since the last allowed one. */
  blocked: number;
  slow: boolean;
  /** What failed, for the fallback card. */
  failure: string | null;
  /** Done by a click rather than a fact, so no fact can take it back. */
  held: boolean;
}

export type DirectorEvent =
  | { type: "SET" }
  | { type: "NEED_FAILED"; need: string }
  | { type: "WORLD"; done: boolean }
  | { type: "ACT"; commits: boolean; completes: boolean }
  | { type: "BLOCKED" }
  | { type: "SLOW" }
  | { type: "TIMEOUT" }
  | { type: "BEAT" }
  | { type: "LEFT" }
  | { type: "STEP"; step: number }
  | { type: "RESIZE" }
  | { type: "DRIFT" }
  | { type: "RETRY" }
  | { type: "EXIT" };

export const EVENT_TYPES: readonly DirectorEvent["type"][] = [
  "SET",
  "NEED_FAILED",
  "WORLD",
  "ACT",
  "BLOCKED",
  "SLOW",
  "TIMEOUT",
  "BEAT",
  "LEFT",
  "STEP",
  "RESIZE",
  "DRIFT",
  "RETRY",
  "EXIT",
];

export const PHASES: readonly Phase[] = [
  "setting",
  "live",
  "pending",
  "done",
  "leaving",
  "failed",
  "exited",
  "finished",
];

export function initialState(step: number, total: number): DirectorState {
  return {
    step: Math.min(Math.max(1, step), total),
    total,
    phase: "setting",
    rev: 0,
    blocked: 0,
    slow: false,
    failure: null,
    held: false,
  };
}

const enter = (state: DirectorState, step: number): DirectorState => ({
  ...initialState(step, state.total),
  rev: state.rev + 1,
});

const fail = (state: DirectorState, failure: string): DirectorState => ({
  ...state,
  phase: "failed",
  slow: false,
  failure,
});

export function transition(
  state: DirectorState,
  event: DirectorEvent,
): DirectorState {
  const { phase } = state;
  if (phase === "exited" || phase === "finished") return state;

  switch (event.type) {
    case "STEP": {
      const step = Math.min(Math.max(1, event.step), state.total);
      return step === state.step ? state : enter(state, step);
    }
    // The final step has no way out but finishing.
    case "EXIT":
      return state.step >= state.total ? state : { ...state, phase: "exited" };
    case "SET":
      return phase === "setting"
        ? { ...state, phase: "live", slow: false, blocked: 0 }
        : state;
    case "NEED_FAILED":
      return phase === "setting" ||
        phase === "live" ||
        phase === "pending" ||
        phase === "leaving"
        ? fail(state, event.need)
        : state;
    case "TIMEOUT":
      return phase === "setting" || phase === "live" || phase === "pending"
        ? fail(state, "timeout")
        : state;
    case "SLOW":
      return (phase === "setting" || phase === "live" || phase === "pending") &&
        !state.slow
        ? { ...state, slow: true }
        : state;
    case "WORLD":
      if (event.done) {
        return phase === "live" || phase === "pending" || phase === "failed"
          ? { ...state, phase: "done", slow: false, failure: null }
          : state;
      }
      // The fact went away during the beat: the step is not done after all.
      return phase === "done" && !state.held
        ? { ...state, phase: "live" }
        : state;
    case "ACT":
      if (phase !== "live") return state;
      if (event.completes)
        return { ...state, phase: "done", held: true, blocked: 0, slow: false };
      if (event.commits) return { ...state, phase: "pending", blocked: 0 };
      return state.blocked === 0 ? state : { ...state, blocked: 0 };
    case "BLOCKED":
      return phase === "live"
        ? { ...state, blocked: state.blocked + 1 }
        : state;
    case "BEAT":
      return phase === "done" ? { ...state, phase: "leaving" } : state;
    case "LEFT":
      return phase === "leaving" && state.step >= state.total
        ? { ...state, phase: "finished" }
        : state;
    case "RESIZE":
    case "DRIFT":
      return phase === "live" || phase === "pending" || phase === "done"
        ? { ...state, rev: state.rev + 1 }
        : state;
    case "RETRY":
      return phase === "failed"
        ? {
            ...state,
            phase: "setting",
            rev: state.rev + 1,
            slow: false,
            failure: null,
            blocked: 0,
            held: false,
          }
        : state;
  }
}
